/**
 * VisualGest — Lista de facturas emitidas (Fase O.8 — 2026-05-14).
 *
 * Lista os documentos emitidos no VisualGest (tabela o_document). Permite
 * descarregar o PDF e reenviar por email. A emissão em si acontece a
 * partir do fluxo de vendas (internas / marketplace) — esta página é só
 * consulta.
 *
 * Backend:
 *   POST /api/v2/olifel/invoices/list
 *   GET  /api/v2/olifel/invoice/pdf?full_id=...
 *   GET  /api/v2/olifel/invoice/send-email?full_id=...&email=...
 */

import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    getOlifelInvoiceList,
    downloadOlifelInvoicePdf,
    sendOlifelInvoiceEmail,
} from "../../services/OlifelServices";

const SOURCE_LABELS = {
    internal_sale: "Venda interna",
    marketplace: "Marketplace",
    ecommerce_api: "E-commerce API",
    manual: "Manual",
};

const STATUS_FILTERS = [
    { value: "", label: "Todos os estados" },
    { value: "issued", label: "Emitidas" },
    { value: "cancelled", label: "Anuladas" },
];

const PAGE_SIZE = 25;

export default function VisualGestInvoice() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = async (targetPage = page) => {
        try {
            setLoading(true);
            const r = await getOlifelInvoiceList({
                status: statusFilter || undefined,
                page: targetPage,
                limit: PAGE_SIZE,
            });
            const data = r.data || {};
            setRows(data.documents || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(
                (err && err.response && err.response.data && err.response.data.message) ||
                "Erro ao carregar facturas"
            );
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(1);
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const handlePage = (e) => {
        const next = e.selected + 1;
        setPage(next);
        load(next);
    };

    const downloadPdf = async (row) => {
        try {
            setBusyId(row.id);
            const r = await downloadOlifelInvoicePdf(row.full_id);
            const blob = new Blob([r.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${String(row.full_id).replace(/[^\w.-]/g, "_")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Não foi possível descarregar o PDF");
        } finally {
            setBusyId(null);
        }
    };

    const sendEmail = async (row) => {
        const email = window.prompt("Email para envio da factura:", row.client_email || "");
        if (!email) return;
        try {
            setBusyId(row.id);
            const r = await sendOlifelInvoiceEmail(row.full_id, email);
            const data = r.data || {};
            if (data.success) {
                toast.success(data.message || "Email enviado");
            } else {
                toast.error(data.message || "Não foi possível enviar o email");
            }
        } catch (err) {
            toast.error(
                (err && err.response && err.response.data && err.response.data.message) ||
                "Erro ao enviar email"
            );
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div>
            <section className="content-header">
                <div className="container-fluid container-custom-height-new">
                    <div className="row mb-2">
                        <div className="col-sm-12 d-flex align-items-center">
                            <button
                                type="button"
                                className="next-button btn btn-secondary"
                                onClick={() => navigate("/visualgest")}
                            >
                                <i className="bi bi-arrow-left"></i> {t("back", { defaultValue: "Voltar" })}
                            </button>
                            <h1 className="page-title ms-auto mb-2">
                                {t("visualgest_invoices_title", {
                                    defaultValue: "VisualGest — Facturas emitidas",
                                })}
                            </h1>
                        </div>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid container-custom-height-new">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card fuelCard">
                                <div className="card-header">
                                    <div className="card-title m-0 float-left" style={{ width: "50%" }}>
                                        <select
                                            className="form-control input-search"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            {STATUS_FILTERS.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="card-title m-0 float-right d-flex">
                                        <button
                                            type="button"
                                            className="btn btn-icon-text mr-2"
                                            onClick={() => load()}
                                            disabled={loading}
                                        >
                                            <i className="bi bi-arrow-repeat"></i>
                                            <span className="ms-2">{t("refresh", { defaultValue: "Actualizar" })}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body fuelCardB">
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th>{t("document", { defaultValue: "Documento" })}</th>
                                                    <th>{t("client", { defaultValue: "Cliente" })}</th>
                                                    <th>{t("total", { defaultValue: "Total" })}</th>
                                                    <th>{t("origin", { defaultValue: "Origem" })}</th>
                                                    <th>{t("status", { defaultValue: "Estado" })}</th>
                                                    <th>{t("date", { defaultValue: "Data" })}</th>
                                                    <th style={{ minWidth: 180 }}>{t("actions", { defaultValue: "Acções" })}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading && (
                                                    <tr><td colSpan={7} className="text-center">A carregar…</td></tr>
                                                )}
                                                {!loading && rows.length === 0 && (
                                                    <tr>
                                                        <td colSpan={7} className="text-center">
                                                            {t("no_records", { defaultValue: "Sem registos" })}
                                                        </td>
                                                    </tr>
                                                )}
                                                {!loading && rows.map((row) => (
                                                    <tr key={row.id}>
                                                        <td>{row.full_id}</td>
                                                        <td>
                                                            {row.client_name || "—"}
                                                            {row.client_nif ? ` (${row.client_nif})` : ""}
                                                        </td>
                                                        <td>
                                                            {row.total != null
                                                                ? `${Number(row.total).toFixed(2)} €`
                                                                : "—"}
                                                        </td>
                                                        <td>{SOURCE_LABELS[row.source] || row.source}</td>
                                                        <td>
                                                            {row.status === "cancelled" ? (
                                                                <span className="text-danger">Anulada</span>
                                                            ) : (
                                                                <span className="text-success">Emitida</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {row.created_at
                                                                ? new Date(row.created_at).toLocaleString("pt-PT")
                                                                : "—"}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex" style={{ gap: 6 }}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    disabled={busyId === row.id}
                                                                    onClick={() => downloadPdf(row)}
                                                                >
                                                                    <i className="bi bi-file-earmark-pdf"></i> PDF
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary"
                                                                    disabled={busyId === row.id}
                                                                    onClick={() => sendEmail(row)}
                                                                >
                                                                    <i className="bi bi-envelope"></i> Email
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="fuelCF">
                                    <div className="card-footer card-footer-part clearfix">
                                        <div className="pagination pagination-sm m-0 float-right">
                                            <ReactPaginate
                                                previousLabel={<i className="bi bi-chevron-double-left"></i>}
                                                nextLabel={<i className="bi bi-chevron-double-right"></i>}
                                                breakLabel={"..."}
                                                pageCount={Math.max(1, Math.ceil(total / PAGE_SIZE))}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={handlePage}
                                                forcePage={page - 1}
                                                containerClassName={"pagination"}
                                                activeClassName={"active"}
                                                pageClassName={"page-item"}
                                                pageLinkClassName={"page-link"}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
